import * as React from "react";
import {
  Typography, Button, Chip, TextField,
  FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Checkbox, Box, Stack, Tabs, Tab, IconButton
} from "../../components/mui";
import {
  SearchRoundedIcon, FilterAltRoundedIcon, RestartAltRoundedIcon,
  GetAppRoundedIcon, CreditScoreRoundedIcon, AssignmentTurnedInRoundedIcon,
  ExpandMoreRoundedIcon, CheckCircleRoundedIcon, CancelRoundedIcon,
  WarningAmberRoundedIcon, SettingsRoundedIcon
} from "../../components/icons";

import "../../styles/main.css";
import "../../styles/pages/condoPayment.css";
import PageLayout from "../../components/shared/PageLayout";
import PageHeader from "../../components/shared/PageHeader";
import DataTableCard from "../../components/shared/DataTableCard";
import {
  CondoPaymentFormModal,
  MiscPaymentFormModal,
  UtilityPaymentFormModal,
  SecurityDepositRefundModal,
  GenerateMonthlyAllowanceModal,
  AllowanceFormModal
} from "./modals";
import ConfirmDialog from "../../components/shared/ConfirmDialog";
import { useToast } from "../../components/shared/ToastProvider";
import { ALLOWANCE_ROWS } from "../condo-units/condoUnitDetailMocks";

// Hardcoded data - Backend developer will replace with API calls
const TABS = [
  { value: "rental", label: "Rental" },
  { value: "misc-expenses", label: "Misc Expenses" },
  { value: "utilities", label: "Utilities" },
  { value: "security-deposit", label: "Security Deposit" },
  { value: "allowance", label: "Allowance" }
];

const FILTER_OPTIONS = {
  condo: [
    { value: "all", label: "All" },
    { value: "oriental", label: "Oriental KH" },
    { value: "141", label: "141 Apartment" }
  ],
  condoUnit: [
    { value: "all", label: "All" }
  ],
  lessee: [
    { value: "all", label: "All" }
  ],
  lessor: [
    { value: "all", label: "All" }
  ],
  active: [
    { value: "all", label: "All" },
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" }
  ],
  paid: [
    { value: "all", label: "All" },
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" }
  ],
  checkReleased: [
    { value: "all", label: "All" },
    { value: "yes", label: "Yes" },
    { value: "no", label: "No" }
  ],
  expenseType: [
    { value: "all", label: "All" }
  ],
  condoCompany: [
    { value: "all", label: "All" }
  ],
  condoSharing: [
    { value: "all", label: "All" },
    { value: "not-shared", label: "Not Shared" },
    { value: "shared", label: "Shared" }
  ],
  monthDue: [
    { value: "all", label: "All" }
  ],
  yearDue: [
    { value: "all", label: "All" }
  ]
};

// Hardcoded condo unit info (UI only for the payment form)
const CONDO_UNIT_INFO_ITEMS = [
  { label: "Condo", value: "Jazz Residences" },
  { label: "Parking Slots", value: "0" },
  { label: "Parking Slot Details", value: "-" },
  { label: "Maid Name", value: "-" },
  { label: "Maid Number", value: "-" },
  { label: "Address", value: "Jupiter Salcedo Village, Makati City" },
  { label: "Unit", value: "1024 Jazz Residences" },
  { label: "Rooms", value: "0" },
  { label: "FurnishType", value: "Fully Furnished" },
  { label: "Occupants / Capacity", value: "0 / 0" },
  { label: "Unit Phone", value: "-" },
  { label: "Floor Area", value: "-" },
  { label: "Condo Company", value: "Caspo" },
  { label: "Unit Leader", value: "-" },
  { label: "Allowance", value: "PHP 0.00" },
  { label: "Condo Sharing", value: "Not Shared" },
  { label: "In-Charge", value: "-" }
];

// Hardcoded payment data (rental tab)
const PAYMENT_DATA = [
  {
    id: 1,
    action: "Edit Payment",
    unit: "1024 Jazz Residences",
    contact: "Simon La'O",
    lessee: "Lessee",
    contract: "Monthly Rent",
    amountDue: "PHP 96,000.00",
    period: "Jan 26 - Feb 27 2022",
    monthlyPayment: "PHP 1,540,000.00",
    checkRelease: "Amount Due",
    status: "success" // green
  },
  {
    id: 2,
    action: "Add Payment",
    unit: "22 Ziper Street",
    contact: "Simon La'O",
    lessee: "Lessee",
    contract: "Monthly Rent",
    amountDue: "PHP 96,000.00",
    period: "Jan 01 - Jun 30 2022",
    monthlyPayment: "PHP 45,000.00",
    checkRelease: "Amount Due",
    status: "error" // red
  }
];

// Hardcoded security deposit data (security deposit tab)
const SECURITY_DEPOSIT_DATA = [
  {
    id: 1,
    condo: "J Tower 1",
    condoUnit: "8A6 J Tower 1",
    lessee: "JK",
    lessor: "J",
    contact: "JK",
    contractStartDate: "15/11/2024",
    contractEndDate: "14/11/2025",
    terminationDate: "14/11/2025",
    securityDeposit: "USD 1,300.00",
    returnedAmount: "USD 0.00",
    expense: "",
    skipped: "No"
  },
  {
    id: 2,
    condo: "J Tower 1",
    condoUnit: "15A3 J Tower 1",
    lessee: "JK",
    lessor: "J",
    contact: "JK",
    contractStartDate: "15/11/2024",
    contractEndDate: "14/11/2025",
    terminationDate: "14/11/2025",
    securityDeposit: "USD 1,300.00",
    returnedAmount: "USD 0.00",
    expense: "",
    skipped: "No"
  }
];

// Hardcoded misc expenses data (misc expenses tab)
const MISC_EXPENSE_DATA = [
  {
    id: 1,
    unit: "2368 Mabolo St.",
    condo: "Dasmarinas Village",
    company: "Caspo",
    sharing: "Not Shared",
    period: "Jan 01 - Jan 31 2022",
    amountDue: "PHP 10,000.00",
    expenseType: "Association Dues",
    billingNo: "BILL-0001",
    payableTo: "Dasmarinas Village Condo Corp.",
    acctEntryDate: "01/10/2023",
    paymentDate: "01/10/2023",
    rfNo: "12028",
    checkRelease: "Check not released",
    checkNo: "-",
    released: "No",
    releasedBy: "-",
    releasedOn: "-",
    remarks: "Pending billing confirmation"
  },
  {
    id: 2,
    unit: "1024 Jazz Residences",
    condo: "Jazz Residences",
    company: "Caspo",
    sharing: "Not Shared",
    period: "Feb 01 - Feb 28 2022",
    amountDue: "PHP 15,000.00",
    expenseType: "Repairs & Maintenance",
    billingNo: "BILL-0002",
    payableTo: "Jazz Residences Condo Corp.",
    acctEntryDate: "01/10/2023",
    paymentDate: "01/10/2023",
    rfNo: "12029",
    checkRelease: "Check not released",
    checkNo: "CHK-2022-001",
    released: "Yes",
    releasedBy: "John Doe",
    releasedOn: "20/02/2022",
    remarks: "For review by accounting"
  }
];

// Hardcoded utilities data (utilities tab)
const UTILITY_PAYMENT_DATA = [
  {
    id: 1,
    unit: "1024 Jazz Residences",
    condo: "Jazz Residences",
    company: "Caspo",
    sharing: "Not Shared",
    period: "16/09/2024 - 15/10/2024",
    amountDue: "PHP 6,546.06",
    utilityType: "Electricity",
    billingNo: "BILL-U-0001",
    payableTo: "Meralco",
    acctEntryDate: "01/10/2024",
    paymentDate: "29/10/2024",
    rfNo: "U-2024-001",
    checkRelease: "Check not released",
    checkNo: "-",
    released: "No",
    releasedBy: "-",
    releasedOn: "-",
    remarks: ""
  },
  {
    id: 2,
    unit: "1024 Jazz Residences",
    condo: "Jazz Residences",
    company: "Caspo",
    sharing: "Not Shared",
    period: "16/08/2024 - 15/09/2024",
    amountDue: "PHP 10,179.69",
    utilityType: "Electricity",
    billingNo: "BILL-U-0002",
    payableTo: "Meralco",
    acctEntryDate: "01/09/2024",
    paymentDate: "04/10/2024",
    rfNo: "U-2024-002",
    checkRelease: "Check not released",
    checkNo: "-",
    released: "No",
    releasedBy: "-",
    releasedOn: "-",
    remarks: ""
  }
];

function CondoPaymentSelectFilter({ label, optionKey }) {
  const options = FILTER_OPTIONS[optionKey] || [];

  return (
    <FormControl size="small" className="condo-payment-filter">
      <InputLabel>{label}</InputLabel>
      <Select defaultValue="all" label={label}>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function CondoPaymentFilterActions() {
  return (
    <Box className="condo-payment-toolbar condo-payment-toolbar--filters">
      <Button variant="contained" startIcon={<SearchRoundedIcon />}>
        Search
      </Button>
      <Button variant="outlined" startIcon={<RestartAltRoundedIcon />}>
        Reset filters
      </Button>
      <Button variant="outlined" startIcon={<GetAppRoundedIcon />}>
        Export
      </Button>
    </Box>
  );
}

function CondoPaymentCondoSummaryCell({ condo, unit, company, sharing }) {
  return (
    <Box className="misc-condo-summary">
      <Box className="misc-condo-summary-row">
        <span className="misc-condo-summary-label">Condo</span>
        <span className="misc-condo-summary-value">{condo}</span>
      </Box>
      <Box className="misc-condo-summary-row">
        <span className="misc-condo-summary-label">Unit</span>
        <span className="misc-condo-summary-value">{unit}</span>
      </Box>
      <Box className="misc-condo-summary-row">
        <span className="misc-condo-summary-label">Company</span>
        <span className="misc-condo-summary-value">{company}</span>
      </Box>
      <Box className="misc-condo-summary-row">
        <span className="misc-condo-summary-label">Sharing</span>
        <span className="misc-condo-summary-value">{sharing}</span>
      </Box>
    </Box>
  );
}

function RentalTabSection({ rows, expandedRow, onToggleRow, onOpenPaymentModal }) {
  return (
    <>
      <TableHead>
        <TableRow>
          <TableCell></TableCell>
          <TableCell padding="checkbox">
            <Checkbox size="small" />
          </TableCell>
          <TableCell></TableCell>
          <TableCell>Condo Unit</TableCell>
          <TableCell>Contact</TableCell>
          <TableCell>Contract</TableCell>
          <TableCell colSpan={2}>Monthly Payment</TableCell>
          <TableCell>Check Release</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <React.Fragment key={row.id}>
            <TableRow hover>
              <TableCell className="action-cell">
                <IconButton
                  size="small"
                  onClick={() => onToggleRow(row.id)}
                  sx={{
                    color: "var(--color-brand-primary)",
                    transform: expandedRow === row.id ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s"
                  }}
                >
                  <ExpandMoreRoundedIcon />
                </IconButton>
              </TableCell>
              <TableCell padding="checkbox">
                <Checkbox size="small" />
              </TableCell>
              <TableCell className="action-cell">
                <Button
                  variant="text"
                  size="small"
                  className="action-button"
                  onClick={() => onOpenPaymentModal(row)}
                >
                  {row.action}
                </Button>
              </TableCell>
              <TableCell>
                <Box className="cell-content-stacked">
                  <Typography variant="caption" className="cell-label">
                    UNIT
                  </Typography>
                  <Typography variant="body2" className="cell-value">
                    {row.unit}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Box className="cell-content-stacked">
                  <Typography variant="caption" className="cell-label">
                    LESSOR
                  </Typography>
                  <Typography variant="body2" className="cell-value">
                    {row.contact}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Box className="cell-content-stacked">
                  <Typography variant="caption" className="cell-label">
                    {row.contract}
                  </Typography>
                  <Typography variant="body2" className="cell-value">
                    {row.amountDue}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell colSpan={2}>
                <Box className="monthly-payment-cell">
                  <Box className="cell-content-stacked">
                    <Typography variant="caption" className="cell-label">
                      AMOUNT DUE
                    </Typography>
                    <Typography
                      variant="body2"
                      className={`cell-value ${row.status ? `amount-${row.status}` : ""}`}
                    >
                      {row.monthlyPayment}
                    </Typography>
                  </Box>
                  <Box className="cell-content-stacked">
                    <Typography variant="caption" className="cell-label">
                      PERIOD
                    </Typography>
                    <Typography variant="body2" className="cell-value">
                      {row.period}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
              <TableCell padding="checkbox">
                <Checkbox size="small" />
              </TableCell>
            </TableRow>

            {expandedRow === row.id && (
              <TableRow>
                <TableCell colSpan={9} className="expanded-row-cell">
                  <Box className="expanded-row-content">
                    <Box className="expanded-row-grid">
                      <Box className="expanded-row-item">
                        <Typography variant="caption" className="expanded-row-label">
                          Unit Phone:
                        </Typography>
                        <Typography variant="body2">n/a</Typography>
                      </Box>
                      <Box className="expanded-row-item">
                        <Typography variant="caption" className="expanded-row-label">
                          Capacity:
                        </Typography>
                        <Typography variant="body2">0</Typography>
                      </Box>
                      <Box className="expanded-row-item">
                        <Typography variant="caption" className="expanded-row-label">
                          Contact:
                        </Typography>
                        <Typography variant="body2">{row.contact}</Typography>
                      </Box>
                      <Box className="expanded-row-item">
                        <Typography variant="caption" className="expanded-row-label">
                          Lessee:
                        </Typography>
                        <Typography variant="body2">Cleo Cortil</Typography>
                      </Box>
                      <Box className="expanded-row-item">
                        <Typography variant="caption" className="expanded-row-label">
                          Start Date:
                        </Typography>
                        <Typography variant="body2">28/01/2022</Typography>
                      </Box>
                      <Box className="expanded-row-item">
                        <Typography variant="caption" className="expanded-row-label">
                          End Date:
                        </Typography>
                        <Typography variant="body2">30/09/2025</Typography>
                      </Box>
                      <Box className="expanded-row-item">
                        <Typography variant="caption" className="expanded-row-label">
                          Month(s):
                        </Typography>
                        <Typography variant="body2">1 Month(s)</Typography>
                      </Box>
                      <Box className="expanded-row-item">
                        <Typography variant="caption" className="expanded-row-label">
                          RF No:
                        </Typography>
                        <Typography variant="body2">12028</Typography>
                      </Box>
                      <Box className="expanded-row-item">
                        <Typography variant="caption" className="expanded-row-label">
                          Acct Entry Date:
                        </Typography>
                        <Typography variant="body2">28/01/2022</Typography>
                      </Box>
                      <Box className="expanded-row-item">
                        <Typography variant="caption" className="expanded-row-label">
                          Payment Date:
                        </Typography>
                        <Typography variant="body2">15/02/2022</Typography>
                      </Box>
                      <Box className="expanded-row-item">
                        <Typography variant="caption" className="expanded-row-label">
                          Receipt No:
                        </Typography>
                        <Typography variant="body2">RCP-2022-001</Typography>
                      </Box>
                      <Box className="expanded-row-item">
                        <Typography variant="caption" className="expanded-row-label">
                          Mode of Payment:
                        </Typography>
                        <Typography variant="body2">1 month</Typography>
                      </Box>
                      <Box className="expanded-row-item">
                        <Typography variant="caption" className="expanded-row-label">
                          Condo Company:
                        </Typography>
                        <Typography variant="body2">Oriental KH Management</Typography>
                      </Box>
                      <Box className="expanded-row-item">
                        <Typography variant="caption" className="expanded-row-label">
                          Condo Sharing:
                        </Typography>
                        <Typography variant="body2">No</Typography>
                      </Box>
                      <Box className="expanded-row-item">
                        <Typography variant="caption" className="expanded-row-label">
                          Check No:
                        </Typography>
                        <Typography variant="body2">CHK-789456</Typography>
                      </Box>
                      <Box className="expanded-row-item">
                        <Typography variant="caption" className="expanded-row-label">
                          Released By:
                        </Typography>
                        <Typography variant="body2">John Doe</Typography>
                      </Box>
                      <Box className="expanded-row-item">
                        <Typography variant="caption" className="expanded-row-label">
                          Released On:
                        </Typography>
                        <Typography variant="body2">20/02/2022</Typography>
                      </Box>
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </React.Fragment>
        ))}
      </TableBody>
    </>
  );
}

function SecurityDepositTabSection({ rows, onRefund }) {
  return (
    <>
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            <Checkbox size="small" />
          </TableCell>
          <TableCell>Action</TableCell>
          <TableCell>Condo</TableCell>
          <TableCell>Condo Unit</TableCell>
          <TableCell>Lessee</TableCell>
          <TableCell>Lessor</TableCell>
          <TableCell>Contact</TableCell>
          <TableCell>Contract Start Date</TableCell>
          <TableCell>Contract End Date</TableCell>
          <TableCell>Termination Date</TableCell>
          <TableCell>Security Deposit</TableCell>
          <TableCell>Returned Amount</TableCell>
          <TableCell>Expense</TableCell>
          <TableCell>Skipped</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow hover key={row.id}>
            <TableCell padding="checkbox">
              <Checkbox size="small" />
            </TableCell>
            <TableCell className="action-cell">
              <Button
                variant="text"
                size="small"
                className="action-button"
                onClick={() => onRefund(row)}
              >
                Refund
              </Button>
            </TableCell>
            <TableCell>{row.condo}</TableCell>
            <TableCell>{row.condoUnit}</TableCell>
            <TableCell>{row.lessee}</TableCell>
            <TableCell>{row.lessor}</TableCell>
            <TableCell>{row.contact}</TableCell>
            <TableCell>{row.contractStartDate}</TableCell>
            <TableCell>{row.contractEndDate}</TableCell>
            <TableCell>{row.terminationDate}</TableCell>
            <TableCell className="security-deposit-amount">{row.securityDeposit}</TableCell>
            <TableCell className="security-deposit-amount">{row.returnedAmount}</TableCell>
            <TableCell>{row.expense}</TableCell>
            <TableCell>{row.skipped}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </>
  );
}

function MiscExpensesTabSection({ rows, onEdit, onDelete }) {
  return (
    <>
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            <Checkbox size="small" />
          </TableCell>
          <TableCell>Actions</TableCell>
          <TableCell>Condo Unit</TableCell>
          <TableCell>Acct Entry Date</TableCell>
          <TableCell>Payment Date</TableCell>
          <TableCell>Period Covered</TableCell>
          <TableCell>Expense Type</TableCell>
          <TableCell>Amount</TableCell>
          <TableCell>Billing No</TableCell>
          <TableCell>Payable To</TableCell>
          <TableCell>RF No</TableCell>
          <TableCell>Check Release</TableCell>
          <TableCell>Remarks</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow hover key={row.id}>
            <TableCell padding="checkbox">
              <Checkbox size="small" />
            </TableCell>
            <TableCell className="action-cell">
              <Button
                variant="text"
                size="small"
                className="action-button"
                onClick={() => onEdit(row)}
              >
                Edit
              </Button>
              <Button
                variant="text"
                size="small"
                className="action-button"
                onClick={() => onDelete(row)}
              >
                Delete
              </Button>
            </TableCell>
            <TableCell>
              <CondoPaymentCondoSummaryCell
                condo={row.condo}
                unit={row.unit}
                company={row.company}
                sharing={row.sharing}
              />
            </TableCell>
            <TableCell>{row.acctEntryDate}</TableCell>
            <TableCell>{row.paymentDate}</TableCell>
            <TableCell>{row.period}</TableCell>
            <TableCell>{row.expenseType}</TableCell>
            <TableCell>{row.amountDue}</TableCell>
            <TableCell>{row.billingNo}</TableCell>
            <TableCell>{row.payableTo}</TableCell>
            <TableCell>{row.rfNo}</TableCell>
            <TableCell>
              <CondoPaymentCheckReleaseSummaryCell
                checkNo={row.checkNo}
                released={row.released}
                releasedBy={row.releasedBy}
                releasedOn={row.releasedOn}
              />
            </TableCell>
            <TableCell>{row.remarks}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </>
  );
}

function UtilitiesTabSection({ rows, onEdit, onDelete }) {
  return (
    <>
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            <Checkbox size="small" />
          </TableCell>
          <TableCell>Actions</TableCell>
          <TableCell>Condo Unit</TableCell>
          <TableCell>Acct Entry Date</TableCell>
          <TableCell>Payment Date</TableCell>
          <TableCell>Period Covered</TableCell>
          <TableCell>Utility Type</TableCell>
          <TableCell>Amount</TableCell>
          <TableCell>Billing No</TableCell>
          <TableCell>Payable To</TableCell>
          <TableCell>RF No</TableCell>
          <TableCell>Check Release</TableCell>
          <TableCell>Remarks</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow hover key={row.id}>
            <TableCell padding="checkbox">
              <Checkbox size="small" />
            </TableCell>
            <TableCell className="action-cell">
              <Button
                variant="text"
                size="small"
                className="action-button"
                onClick={() => onEdit(row)}
              >
                Edit
              </Button>
              <Button
                variant="text"
                size="small"
                className="action-button"
                onClick={() => onDelete(row)}
              >
                Delete
              </Button>
            </TableCell>
            <TableCell>
              <CondoPaymentCondoSummaryCell
                condo={row.condo}
                unit={row.unit}
                company={row.company}
                sharing={row.sharing}
              />
            </TableCell>
            <TableCell>{row.acctEntryDate}</TableCell>
            <TableCell>{row.paymentDate}</TableCell>
            <TableCell>{row.period}</TableCell>
            <TableCell>{row.utilityType}</TableCell>
            <TableCell>{row.amountDue}</TableCell>
            <TableCell>{row.billingNo}</TableCell>
            <TableCell>{row.payableTo}</TableCell>
            <TableCell>{row.rfNo}</TableCell>
            <TableCell>
              <CondoPaymentCheckReleaseSummaryCell
                checkNo={row.checkNo}
                released={row.released}
                releasedBy={row.releasedBy}
                releasedOn={row.releasedOn}
              />
            </TableCell>
            <TableCell>{row.remarks}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </>
  );
}

function AllowanceTabSection({ rows, onEdit, onDelete }) {
  return (
    <>
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            <Checkbox size="small" />
          </TableCell>
          <TableCell>Actions</TableCell>
          <TableCell>EID</TableCell>
          <TableCell>Full Name</TableCell>
          <TableCell>Condo</TableCell>
          <TableCell>Condo Unit</TableCell>
          <TableCell>Payment for</TableCell>
          <TableCell>Payment To</TableCell>
          <TableCell>Amount</TableCell>
          <TableCell>Condo Company</TableCell>
          <TableCell>Condo Sharing</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, index) => (
          <TableRow hover key={index}>
            <TableCell padding="checkbox">
              <Checkbox size="small" />
            </TableCell>
            <TableCell className="action-cell">
              <Button
                variant="text"
                size="small"
                className="action-button"
                onClick={() => onEdit(row)}
              >
                Edit
              </Button>
              <Button
                variant="text"
                size="small"
                className="action-button"
                onClick={() => onDelete(row)}
              >
                Delete
              </Button>
            </TableCell>
            <TableCell>{row.eid || "-"}</TableCell>
            <TableCell>{row.fullName || "-"}</TableCell>
            <TableCell>{row.condo || "-"}</TableCell>
            <TableCell>{row.condoUnit || "-"}</TableCell>
            <TableCell>{row.payment}</TableCell>
            <TableCell>{row.payableTo}</TableCell>
            <TableCell className="amount-cell">{row.amount}</TableCell>
            <TableCell>{row.condoCompany}</TableCell>
            <TableCell>{row.condoSharing}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </>
  );
}

function CondoPaymentCheckReleaseSummaryCell({ checkNo, released, releasedBy, releasedOn }) {
  return (
    <Box className="misc-condo-summary">
      <Box className="misc-condo-summary-row">
        <span className="misc-condo-summary-label">Check No</span>
        <span className="misc-condo-summary-value">{checkNo}</span>
      </Box>
      <Box className="misc-condo-summary-row">
        <span className="misc-condo-summary-label">Released</span>
        <span className="misc-condo-summary-value">{released}</span>
      </Box>
      <Box className="misc-condo-summary-row">
        <span className="misc-condo-summary-label">Released By</span>
        <span className="misc-condo-summary-value">{releasedBy}</span>
      </Box>
      <Box className="misc-condo-summary-row">
        <span className="misc-condo-summary-label">Released On</span>
        <span className="misc-condo-summary-value">{releasedOn}</span>
      </Box>
    </Box>
  );
}

/**
 * Condo Payment Page Component
 * UI/Layout only - Backend developer will add:
 * - API calls for fetching payment data
 * - Tab switching functionality
 * - Filter functionality
 * - Search functionality
 * - Pagination logic
 * - Assign to RF functionality
 * - Export functionality
 * - Payment status updates
 */
export default function CondoPayment({ onLogout, onNavigate }) {
  // Tab state - UI only for now
  const [activeTab, setActiveTab] = React.useState("rental");
  const assignToRF = "12028";
  const { showToast } = useToast();

  // Expandable row state - Backend developer will add proper state management
  const [expandedRow, setExpandedRow] = React.useState(null);

  // Payment form modal state (UI-only)
  const [isPaymentModalOpen, setIsPaymentModalOpen] = React.useState(false);
  const [paymentFormMode, setPaymentFormMode] = React.useState("edit");
  const [selectedPaymentRow, setSelectedPaymentRow] = React.useState(null);

  // Security deposit refund modal state (UI-only)
  const [isSecurityRefundModalOpen, setIsSecurityRefundModalOpen] = React.useState(false);
  const [selectedSecurityDepositRow, setSelectedSecurityDepositRow] = React.useState(null);

  // Misc expense payment modal state (UI-only)
  const [isMiscPaymentModalOpen, setIsMiscPaymentModalOpen] = React.useState(false);
  const [miscPaymentFormMode, setMiscPaymentFormMode] = React.useState("add"); // "add" | "edit"
  const [selectedMiscPaymentRow, setSelectedMiscPaymentRow] = React.useState(null);
  const [isMiscDeleteOpen, setIsMiscDeleteOpen] = React.useState(false);
  const [miscRowToDelete, setMiscRowToDelete] = React.useState(null);

  // Utility payment modal state (UI-only)
  const [isUtilityPaymentModalOpen, setIsUtilityPaymentModalOpen] = React.useState(false);
  const [utilityPaymentFormMode, setUtilityPaymentFormMode] = React.useState("add"); // "add" | "edit"
  const [selectedUtilityPaymentRow, setSelectedUtilityPaymentRow] = React.useState(null);
  const [isUtilityDeleteOpen, setIsUtilityDeleteOpen] = React.useState(false);
  const [utilityRowToDelete, setUtilityRowToDelete] = React.useState(null);

  // Allowance modal state (UI-only)
  const [isAllowanceModalOpen, setIsAllowanceModalOpen] = React.useState(false);
  const [allowanceFormMode, setAllowanceFormMode] = React.useState("add"); // "add" | "edit"
  const [selectedAllowanceRow, setSelectedAllowanceRow] = React.useState(null);
  const [isAllowanceDeleteOpen, setIsAllowanceDeleteOpen] = React.useState(false);
  const [allowanceRowToDelete, setAllowanceRowToDelete] = React.useState(null);

  // Generate Monthly Allowance modal (UI-only)
  const [isGenerateAllowanceModalOpen, setIsGenerateAllowanceModalOpen] = React.useState(false);

  const handleToggleRow = (rowId) => {
    setExpandedRow(expandedRow === rowId ? null : rowId);
  };

  const handleOpenAllowanceModal = (row, mode) => {
    setSelectedAllowanceRow(row || null);
    setAllowanceFormMode(mode || (row ? "edit" : "add"));
    setIsAllowanceModalOpen(true);
  };

  const handleCloseAllowanceModal = () => {
    setIsAllowanceModalOpen(false);
    setSelectedAllowanceRow(null);
  };

  const handleOpenGenerateAllowanceModal = () => {
    setIsGenerateAllowanceModalOpen(true);
  };

  const handleCloseGenerateAllowanceModal = () => {
    setIsGenerateAllowanceModalOpen(false);
  };

  const handleOpenPaymentModal = (row, modeOverride) => {
    setSelectedPaymentRow(row || null);
    setPaymentFormMode(
      modeOverride || (row && row.action === "Add Payment" ? "create" : "edit")
    );
    setIsPaymentModalOpen(true);
  };

  const handleClosePaymentModal = () => {
    setIsPaymentModalOpen(false);
    setSelectedPaymentRow(null);
  };

  const handleOpenMiscPaymentModal = (row, mode) => {
    setSelectedMiscPaymentRow(row || null);
    setMiscPaymentFormMode(mode || (row ? "edit" : "add"));
    setIsMiscPaymentModalOpen(true);
  };

  const handleCloseMiscPaymentModal = () => {
    setIsMiscPaymentModalOpen(false);
    setSelectedMiscPaymentRow(null);
  };

  const handleOpenUtilityPaymentModal = (row, mode) => {
    setSelectedUtilityPaymentRow(row || null);
    setUtilityPaymentFormMode(mode || (row ? "edit" : "add"));
    setIsUtilityPaymentModalOpen(true);
  };

  const handleCloseUtilityPaymentModal = () => {
    setIsUtilityPaymentModalOpen(false);
    setSelectedUtilityPaymentRow(null);
  };

  const handleOpenSecurityRefundModal = (row) => {
    setSelectedSecurityDepositRow(row || null);
    setIsSecurityRefundModalOpen(true);
  };

  const handleCloseSecurityRefundModal = () => {
    setIsSecurityRefundModalOpen(false);
    setSelectedSecurityDepositRow(null);
  };

  const handleOpenMiscDeleteDialog = (row) => {
    setMiscRowToDelete(row);
    setIsMiscDeleteOpen(true);
  };

  const handleConfirmMiscDelete = () => {
    // UI-only for now; backend will handle actual delete
    setIsMiscDeleteOpen(false);
    setMiscRowToDelete(null);
    if (showToast) {
      showToast({ message: "Misc expense payment deleted", severity: "success" });
    }
  };

  const handleOpenUtilityDeleteDialog = (row) => {
    setUtilityRowToDelete(row);
    setIsUtilityDeleteOpen(true);
  };

  const handleConfirmUtilityDelete = () => {
    // UI-only for now; backend will handle actual delete
    setIsUtilityDeleteOpen(false);
    setUtilityRowToDelete(null);
    if (showToast) {
      showToast({ message: "Utility payment deleted", severity: "success" });
    }
  };

  const handleOpenAllowanceDeleteDialog = (row) => {
    setAllowanceRowToDelete(row);
    setIsAllowanceDeleteOpen(true);
  };

  const handleConfirmAllowanceDelete = () => {
    // UI-only for now; backend will handle actual delete
    setIsAllowanceDeleteOpen(false);
    setAllowanceRowToDelete(null);
    if (showToast) {
      showToast({ message: "Allowance record deleted", severity: "success" });
    }
  };

  return (
    <PageLayout activeKey="condo-payment" onLogout={onLogout} onNavigate={onNavigate} className="page-content">
      <PageHeader
        icon={CreditScoreRoundedIcon}
        title="Condo Payment"
        summary="Track and manage all condo-related payments including rent, utilities, and expenses."
      />

      {/* Tabs */}
      <Box className="tabs-container">
        <Tabs
          value={activeTab}
          onChange={(event, value) => setActiveTab(value)}
        >
          {TABS.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={tab.label}
            />
          ))}
        </Tabs>
      </Box>

      {/* Toolbar under tabs: Add buttons only (shown only on tabs that support Add) */}
      {(activeTab === "misc-expenses" || activeTab === "utilities" || activeTab === "allowance") && (
        <Box className="condo-payment-toolbar" display="flex" justifyContent="flex-start" gap={1.5} mb={0.5}>
          {activeTab === "misc-expenses" && (
            <Button
              variant="contained"
              onClick={() => handleOpenMiscPaymentModal(null, "add")}
            >
              + Add Misc Expense
            </Button>
          )}
          {activeTab === "utilities" && (
            <Button
              variant="contained"
              onClick={() => handleOpenUtilityPaymentModal(null, "add")}
            >
              + Add Utility Payment
            </Button>
          )}
          {activeTab === "allowance" && (
            <Button
              variant="contained"
              onClick={() => handleOpenAllowanceModal(null, "add")}
            >
              + Add Allowance
            </Button>
          )}
        </Box>
      )}

      {/* Filters */}
      <Box className="content-box condo-payment-filters">
        {activeTab === "security-deposit" ? (
          <>
            {/* Security Deposit filters - 3 fields */}
            <Box className="condo-payment-filters-row">
              <CondoPaymentSelectFilter label="Condo" optionKey="condo" />
              <CondoPaymentSelectFilter label="Condo Unit" optionKey="condoUnit" />

              <FormControl size="small" className="condo-payment-filter">
                <InputLabel>Security Deposit Returned</InputLabel>
                <Select defaultValue="all" label="Security Deposit Returned">
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="yes">Yes</MenuItem>
                  <MenuItem value="no">No</MenuItem>
                </Select>
              </FormControl>

              {/* Empty cells to keep grid alignment */}
              <div className="condo-payment-filter" />
              <div className="condo-payment-filter" />
            </Box>

            {/* Actions row under filters */}
            <CondoPaymentFilterActions />

            {/* Ignore Alert row - styled similar to assign row separator */}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              className="condo-payment-assign-row security-deposit-alert-row"
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<WarningAmberRoundedIcon sx={{ color: "#ffb300" }} />}
                className="security-deposit-alert-button"
              >
                Ignore Alert
              </Button>
            </Stack>
          </>
        ) : activeTab === "misc-expenses" || activeTab === "utilities" ? (
          <>
            {/* Row 1 - Misc/Utilities filters */}
            <Box className="condo-payment-filters-row">
              <CondoPaymentSelectFilter label="Condo" optionKey="condo" />
              <CondoPaymentSelectFilter label="Condo Unit" optionKey="condoUnit" />
              <CondoPaymentSelectFilter label="Expense Type" optionKey="expenseType" />
              <CondoPaymentSelectFilter label="Month Due" optionKey="monthDue" />
              <CondoPaymentSelectFilter label="Year Due" optionKey="yearDue" />
            </Box>

            {/* Row 2 - Misc/Utilities filters */}
            <Box className="condo-payment-filters-row">
              <CondoPaymentSelectFilter label="Paid" optionKey="paid" />
              <CondoPaymentSelectFilter label="Check Released" optionKey="checkReleased" />
              <CondoPaymentSelectFilter label="Condo Company" optionKey="condoCompany" />
              <CondoPaymentSelectFilter label="Condo Sharing" optionKey="condoSharing" />

              {/* Empty cell to keep 5-column grid consistent */}
              <div className="condo-payment-filter" />
            </Box>

            {/* Actions row under filters */}
            <CondoPaymentFilterActions />
          </>
        ) : activeTab === "allowance" ? (
          <>
            {/* Allowance filters - single row */}
            <Box className="condo-payment-filters-row condo-payment-filters-row-allowance">
              <CondoPaymentSelectFilter label="Condo" optionKey="condo" />
              <CondoPaymentSelectFilter label="Condo Unit" optionKey="condoUnit" />
              <CondoPaymentSelectFilter label="Month" optionKey="monthDue" />
              <CondoPaymentSelectFilter label="Year" optionKey="yearDue" />
              <CondoPaymentSelectFilter label="Condo Company" optionKey="condoCompany" />
              <CondoPaymentSelectFilter label="Condo Sharing" optionKey="condoSharing" />
            </Box>

            {/* Actions row under filters */}
            <CondoPaymentFilterActions />

            {/* Generate Monthly Allowances row - reuse horizontal separator style */}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              className="condo-payment-assign-row"
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<SettingsRoundedIcon className="condo-payment-generate-icon" />}
                className="condo-payment-generate-button"
                onClick={handleOpenGenerateAllowanceModal}
              >
                Generate Monthly Allowances
              </Button>
            </Stack>
          </>
        ) : (
          <>
            <Box className="condo-payment-filters-row">
              {/* Row 1 - Rental filters */}
              <CondoPaymentSelectFilter label="Condo" optionKey="condo" />
              <CondoPaymentSelectFilter label="Condo Unit" optionKey="condoUnit" />
              <CondoPaymentSelectFilter label="Lessee" optionKey="lessee" />
              <CondoPaymentSelectFilter label="Lessor" optionKey="lessor" />
              <CondoPaymentSelectFilter label="Active" optionKey="active" />
            </Box>

            <Box className="condo-payment-filters-row">
              {/* Row 2 - Rental filters */}
              <TextField
                type="month"
                label="Contract Date"
                size="small"
                className="condo-payment-filter"
                InputLabelProps={{ shrink: true }}
                placeholder="YYYY-MM"
              />

              <TextField
                type="month"
                label="Acct Entry Date"
                size="small"
                className="condo-payment-filter"
                InputLabelProps={{ shrink: true }}
                placeholder="YYYY-MM"
              />

              <CondoPaymentSelectFilter label="Paid" optionKey="paid" />
              <CondoPaymentSelectFilter label="Check Released" optionKey="checkReleased" />
            </Box>

            {/* Actions row under filters */}
            <CondoPaymentFilterActions />
          </>
        )}

        {/* Check Status and Assign to RF - not shown on Security Deposit or Allowance tabs */}
        {activeTab !== "security-deposit" && activeTab !== "allowance" && (
          <Stack direction="row" spacing={2} alignItems="center" className="condo-payment-assign-row">
            <Button
              variant="outlined"
              size="small"
              className="condo-payment-check-status-btn condo-payment-check-status-btn--released"
              startIcon={<CheckCircleRoundedIcon className="condo-payment-check-status-icon condo-payment-check-status-icon--released" />}
            >
              Set Check as Released
            </Button>
            <Button
              variant="outlined"
              size="small"
              className="condo-payment-check-status-btn condo-payment-check-status-btn--unreleased"
              startIcon={<CancelRoundedIcon className="condo-payment-check-status-icon condo-payment-check-status-icon--unreleased" />}
            >
              Set Check as Unreleased
            </Button>
            
            <Typography variant="body2" className="condo-payment-assign-label">
              Assign To RF No
            </Typography>
            <FormControl size="small" className="condo-payment-assign-select">
              <Select defaultValue="12028">
                <MenuItem value="12028">12028</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" size="small" startIcon={<AssignmentTurnedInRoundedIcon />}>
              Assign
            </Button>
          </Stack>
        )}
      </Box>

      {/* Data Table */}
      <DataTableCard
        title="Payment Records"
        showExport={false}
        totalEntries={2}
        startEntry={1}
        endEntry={2}
        currentPage={1}
        totalPages={1}
      >
        <TableContainer className="data-table-wrapper">
          <Table size="small">
            {activeTab === "rental" ? (
              <RentalTabSection
                rows={PAYMENT_DATA}
                expandedRow={expandedRow}
                onToggleRow={handleToggleRow}
                onOpenPaymentModal={handleOpenPaymentModal}
              />
            ) : activeTab === "security-deposit" ? (
              <SecurityDepositTabSection
                rows={SECURITY_DEPOSIT_DATA}
                onRefund={handleOpenSecurityRefundModal}
              />
            ) : activeTab === "misc-expenses" ? (
              <MiscExpensesTabSection
                rows={MISC_EXPENSE_DATA}
                onEdit={(row) => handleOpenMiscPaymentModal(row, "edit")}
                onDelete={handleOpenMiscDeleteDialog}
              />
            ) : activeTab === "utilities" ? (
              <UtilitiesTabSection
                rows={UTILITY_PAYMENT_DATA}
                onEdit={(row) => handleOpenUtilityPaymentModal(row, "edit")}
                onDelete={handleOpenUtilityDeleteDialog}
              />
            ) : activeTab === "allowance" ? (
              <AllowanceTabSection
                rows={ALLOWANCE_ROWS}
                onEdit={(row) => handleOpenAllowanceModal(row, "edit")}
                onDelete={handleOpenAllowanceDeleteDialog}
              />
            ) : null}
          </Table>
        </TableContainer>
      </DataTableCard>
      <CondoPaymentFormModal
        open={isPaymentModalOpen}
        onClose={handleClosePaymentModal}
        mode={paymentFormMode}
        condoItems={CONDO_UNIT_INFO_ITEMS}
        initialValues={selectedPaymentRow || {}}
      />

      {/* Add/Edit Misc Expense Payment modal (UI-only) */}
      <MiscPaymentFormModal
        open={isMiscPaymentModalOpen}
        onClose={handleCloseMiscPaymentModal}
        mode={miscPaymentFormMode}
        condoItems={CONDO_UNIT_INFO_ITEMS}
        initialValues={selectedMiscPaymentRow || {}}
      />
      {/* Delete Misc Expense Payment confirmation dialog */}
      <ConfirmDialog
        open={isMiscDeleteOpen}
        onClose={() => setIsMiscDeleteOpen(false)}
        onConfirm={handleConfirmMiscDelete}
        title="Delete Record"
        message="Are you sure you want to delete this misc expense payment? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      {/* Add/Edit Utility Payment modal (UI-only) */}
      <UtilityPaymentFormModal
        open={isUtilityPaymentModalOpen}
        onClose={handleCloseUtilityPaymentModal}
        mode={utilityPaymentFormMode}
        initialValues={selectedUtilityPaymentRow || {}}
      />

      {/* Add/Edit Allowance modal (UI-only) */}
      <AllowanceFormModal
        open={isAllowanceModalOpen}
        onClose={handleCloseAllowanceModal}
        mode={allowanceFormMode}
        initialValues={selectedAllowanceRow || {}}
      />

      {/* Delete Utility Payment confirmation dialog */}
      <ConfirmDialog
        open={isUtilityDeleteOpen}
        onClose={() => setIsUtilityDeleteOpen(false)}
        onConfirm={handleConfirmUtilityDelete}
        title="Delete Record"
        message="Are you sure you want to delete this utility payment? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      {/* Delete Allowance record dialog (UI-only) */}
      <ConfirmDialog
        open={isAllowanceDeleteOpen}
        onClose={() => setIsAllowanceDeleteOpen(false)}
        onConfirm={handleConfirmAllowanceDelete}
        title="Delete Record"
        message="Are you sure you want to delete this allowance record? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />

      {/* Security Deposit refund modal (UI-only) */}
      <SecurityDepositRefundModal
        open={isSecurityRefundModalOpen}
        onClose={handleCloseSecurityRefundModal}
        initialValues={selectedSecurityDepositRow || {}}
      />

      {/* Generate Monthly Allowance modal (UI-only) */}
      <GenerateMonthlyAllowanceModal
        open={isGenerateAllowanceModalOpen}
        onClose={handleCloseGenerateAllowanceModal}
      />

    </PageLayout>
  );
}
